import ShopItemDto, { ShopDisplaySection } from '../models/dtos/ShopItemDto';
import { isNewShopSku } from './shopItemPayload';
import { getSectionBadgeLabel } from './shopSectionUi';

export const SHOP_SECTION_ORDER_FALLBACK: ShopDisplaySection[] = [
    'FIRST_PURCHASE',
    'DAILY_DEALS',
    'BUNDLES',
    'COIN_PACKS',
    'FREE_REWARDS',
];

export const DEFAULT_SECTION_DISPLAY_ORDER: ShopDisplaySection[] = [...SHOP_SECTION_ORDER_FALLBACK];

export function normalizeSectionDisplayOrder(
    order?: string[] | ShopDisplaySection[] | null,
): ShopDisplaySection[] {
    const parsed = (order ?? [])
        .map((s) => String(s).trim())
        .filter(Boolean)
        .filter((s): s is ShopDisplaySection =>
            (SHOP_SECTION_ORDER_FALLBACK as string[]).includes(s),
        );
    const unique = Array.from(new Set(parsed));
    const missing = SHOP_SECTION_ORDER_FALLBACK.filter((s) => !unique.includes(s));
    return unique.length > 0 ? [...unique, ...missing] : [...DEFAULT_SECTION_DISPLAY_ORDER];
}

/** Swap [type] with the previous/next *visible* section in the full order list. */
export function moveSectionInOrder(
    order: ShopDisplaySection[],
    type: ShopDisplaySection,
    direction: -1 | 1,
    visibleTypes: ShopDisplaySection[] = order,
): ShopDisplaySection[] {
    const full = normalizeSectionDisplayOrder(order);
    const vIndex = visibleTypes.indexOf(type);
    const other = visibleTypes[vIndex + direction];
    if (!other) return full;
    const i = full.indexOf(type);
    const j = full.indexOf(other);
    if (i < 0 || j < 0) return full;
    const next = [...full];
    next[i] = other;
    next[j] = type;
    return next;
}

export type ShopPreviewStatusFilter = 'active' | 'disabled' | 'all';
export type ShopPreviewCatalogFilter = 'all' | 'new_shop' | 'legacy';

export type ShopPreviewFilters = {
    status: ShopPreviewStatusFilter;
    catalog: ShopPreviewCatalogFilter;
    osType: string; // 'All' | 'iOS' | 'Android'
    section: ShopDisplaySection | 'ALL';
};

export const DEFAULT_SHOP_PREVIEW_FILTERS: ShopPreviewFilters = {
    status: 'active',
    catalog: 'new_shop',
    osType: 'All',
    section: 'ALL',
};

export type ShopPreviewRow = {
    key: string;
    columns: 1 | 2;
    items: ShopItemDto[];
};

export type ShopPreviewSection = {
    type: ShopDisplaySection;
    title: string;
    sortKey: number;
    rows: ShopPreviewRow[];
    /** True when this is the synthetic video slot (not a DB FREE_REWARDS catalog item). */
    isVideoSlot?: boolean;
};

function columnsInRow(item: Pick<ShopItemDto, 'columnsInRow'>): 1 | 2 {
    const value = item.columnsInRow ?? 1;
    return value >= 2 ? 2 : 1;
}

/** Same pairing rules as Android `groupShopItemsIntoRows`. */
export function groupShopItemsIntoRows(items: ShopItemDto[]): ShopPreviewRow[] {
    const rows: ShopPreviewRow[] = [];
    for (const item of items) {
        const cols = columnsInRow(item);
        if (cols === 1) {
            rows.push({
                key: `full-${item.id}`,
                columns: 1,
                items: [item],
            });
            continue;
        }
        const last = rows[rows.length - 1];
        if (last && last.columns === 2 && last.items.length < 2) {
            last.items.push(item);
            last.key = `pair-${last.items.map((i) => i.id).join('-')}`;
        } else {
            rows.push({
                key: `half-${item.id}`,
                columns: 2,
                items: [item],
            });
        }
    }
    return rows;
}

export function sortShopItemsByDisplayOrder(items: ShopItemDto[]): ShopItemDto[] {
    return [...items].sort((a, b) => {
        const orderDiff = (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
        if (orderDiff !== 0) return orderDiff;
        return (b.id ?? 0) - (a.id ?? 0);
    });
}

export function filterShopItemsForPreview(
    items: ShopItemDto[],
    filters: ShopPreviewFilters,
): ShopItemDto[] {
    return items.filter((item) => {
        if (filters.status === 'active' && item.isDeleted) return false;
        if (filters.status === 'disabled' && !item.isDeleted) return false;

        if (filters.catalog === 'new_shop' && !isNewShopSku(item)) return false;
        if (filters.catalog === 'legacy' && isNewShopSku(item)) return false;

        if (filters.osType !== 'All') {
            const want = filters.osType.toLowerCase();
            const got = (item.osType ?? '').toLowerCase();
            if (got !== want && got !== 'all') return false;
        }

        if (filters.section !== 'ALL') {
            const section = item.displaySection ?? 'COIN_PACKS';
            if (section !== filters.section) return false;
        }

        return true;
    });
}

/**
 * Builds the orientative shop layout from already-filtered items.
 * Category order follows [sectionDisplayOrder] (including FREE_REWARDS / video slot).
 * FREE_REWARDS catalog SKUs are omitted; video is a synthetic slot.
 */
export function buildShopLayoutPreview(
    items: ShopItemDto[],
    options?: {
        includeVideoSlot?: boolean;
        sectionDisplayOrder?: string[] | ShopDisplaySection[];
    },
): ShopPreviewSection[] {
    const includeVideoSlot = options?.includeVideoSlot !== false;
    const sectionOrder = normalizeSectionDisplayOrder(options?.sectionDisplayOrder);
    const bySection = new Map<ShopDisplaySection, ShopItemDto[]>();

    for (const item of items) {
        const section = (item.displaySection ?? 'COIN_PACKS') as ShopDisplaySection;
        if (section === 'FREE_REWARDS') continue;
        const list = bySection.get(section) ?? [];
        list.push(item);
        bySection.set(section, list);
    }

    const videoSection: ShopPreviewSection = {
        type: 'FREE_REWARDS',
        title: getSectionBadgeLabel('FREE_REWARDS') ?? 'FREE_REWARDS',
        sortKey: sectionOrder.indexOf('FREE_REWARDS'),
        rows: groupShopItemsIntoRows([
            {
                id: -1,
                betcoins: 0,
                type: 'Bonus',
                effectDuration: 0,
                noAds: false,
                isDeleted: false,
                osType: 'all',
                displaySection: 'FREE_REWARDS',
                displayOrder: 0,
                columnsInRow: 1,
                titleText: 'Video reward',
                storeCode: 'video_reward',
                presentationMode: 'HTML_INLINE',
            },
        ]),
        isVideoSlot: true,
    };

    const result: ShopPreviewSection[] = [];
    for (const type of sectionOrder) {
        if (type === 'FREE_REWARDS') {
            if (includeVideoSlot) result.push(videoSection);
            continue;
        }
        const sectionItems = sortShopItemsByDisplayOrder(bySection.get(type) ?? []);
        if (sectionItems.length === 0) continue;
        const sortKey = Math.min(...sectionItems.map((i) => i.displayOrder ?? 0));
        result.push({
            type,
            title: getSectionBadgeLabel(type) ?? type,
            sortKey,
            rows: groupShopItemsIntoRows(sectionItems),
        });
    }
    return result;
}

export function itemPreviewLabel(item: ShopItemDto): string {
    if (item.id === -1) return 'Video reward';
    const title = item.titleText?.trim();
    if (title) return title.length > 42 ? `${title.slice(0, 40)}…` : title;
    return item.storeCode || `Item #${item.id}`;
}

export function rowWidthHint(row: ShopPreviewRow): string {
    if (row.columns === 1) return 'Full width';
    if (row.items.length === 2) return '2 per row';
    return 'Half width';
}

/** Swap displayOrder between two items (for move up/down). */
export function swapDisplayOrder(a: ShopItemDto, b: ShopItemDto): [ShopItemDto, ShopItemDto] {
    const orderA = a.displayOrder ?? 0;
    const orderB = b.displayOrder ?? 0;
    if (orderA === orderB) {
        // Keep visual order stable by bumping the moved item.
        return [
            { ...a, displayOrder: orderA },
            { ...b, displayOrder: orderB + 1 },
        ];
    }
    return [
        { ...a, displayOrder: orderB },
        { ...b, displayOrder: orderA },
    ];
}
